import { Button } from 'components/button/Button';
import { InputField } from 'components/input/InputField';
import { Formik } from 'components/form/formik';
import diff from 'object-diff';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { useSWRConfig } from 'swr';
import * as Yup from 'yup';

import { updateCargoType, useCargoTypesSuspense, CARGO_TYPES_SWR_KEY } from '../models';
import { getDisplayError } from '../../../utils/get-display-error';
import { nullthrows } from '../../../utils/invariant';
import { captureException } from '@sentry/react';
import { DelayedSpinner } from 'components/Spinner';

type CargoTypeItem = NonNullable<ReturnType<typeof useCargoTypesSuspense>['data']>[number];

const createCargoTypeSchema = Yup.object().shape({
  id: Yup.string().required('Vereist'),
  nameNl: Yup.string().required('Vereist'),
  nameFr: Yup.string().required('Vereist'),
  nameEn: Yup.string().required('Vereist'),
  nameDe: Yup.string().required('Vereist'),
});

interface ICargoTypeValues {
  id: string;
  nameNl: string;
  nameFr: string;
  nameEn: string;
  nameDe: string;
}

interface IUpdateCargoTypeFormProps {
  id: string;
  onComplete?: (values: ICargoTypeValues) => void;
  onCancel?: () => void;
}

export const UpdateCargoTypeForm: React.FC<IUpdateCargoTypeFormProps> = (props) => {
  const { id, onComplete, onCancel } = props;
  const { data, isLoading } = useCargoTypesSuspense();

  if (isLoading) {
    return <DelayedSpinner size={8} className="flex justify-center items-center p-8" />;
  }

  const cargoType = nullthrows(
    data?.find((v) => v.id === id),
    'Package type not found',
  );
  return <CargoTypeForm cargoType={cargoType} onComplete={onComplete} onCancel={onCancel} />;
};

const CargoTypeForm: React.FC<{
  cargoType: CargoTypeItem;
  onComplete?: (values: ICargoTypeValues) => void;
  onCancel?: () => void;
}> = ({ cargoType, onComplete, onCancel }) => {
  const { mutate } = useSWRConfig();

  const initialValues: ICargoTypeValues = useMemo(() => {
    return {
      id: cargoType.id,
      nameNl: cargoType.nameNl,
      nameFr: cargoType.nameFr,
      nameEn: cargoType.nameEn,
      nameDe: cargoType.nameDe,
    };
  }, [cargoType]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={createCargoTypeSchema}
      onSubmit={async (values) => {
        try {
          const patch: Partial<typeof values> = diff(initialValues, values);
          await updateCargoType(cargoType.id, patch);
          await mutate(CARGO_TYPES_SWR_KEY);
          onComplete?.(values);
          toast.success('Inhoud type aangepast');
        } catch (err: any) {
          captureException(err);
          toast.error('Kon inhoud type niet aanpassen: ' + getDisplayError(err));
        }
      }}
    >
      {({ handleSubmit, isSubmitting, submitForm }) => {
        return (
          <form onSubmit={handleSubmit}>
            <InputField labelText="Code" type="text" name="id" />
            <InputField labelText="Naam NL" type="text" name="nameNl" />
            <InputField labelText="Naam EN" type="text" name="nameEn" />
            <InputField labelText="Naam FR" type="text" name="nameFr" />
            <InputField labelText="Naam DE" type="text" name="nameDe" />

            <div className="flex justify-between">
              {onCancel && (
                <div>
                  <Button isDisabled={isSubmitting} isLoading={isSubmitting} onTrigger={onCancel}>
                    Annuleer
                  </Button>
                </div>
              )}
              <Button
                type="submit"
                color="primary"
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
                onTrigger={submitForm}
              >
                Pas inhoudstype aan
              </Button>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};
